import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

function CourseCard({ course, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="h-full"
    >
      <Link
        to={`/programs/${course.slug}`}
        data-testid={`course-card-${course.slug}`}
        className="group flex flex-col h-full bg-lima-ink border border-black hover:-translate-y-1 transition-transform duration-300"
      >
        <div className="relative h-56 md:h-64 overflow-hidden">
          <img
            src={course.image}
            alt={course.title}
            loading="lazy"
            className="w-full h-full object-cover saturate-[.65] opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-500"
          />
          <span className="absolute top-4 left-4 font-mono2 text-[10px] text-white/80">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="absolute top-4 right-4 font-mono2 text-[10px] tracking-[0.2em] text-white/70 bg-black/60 backdrop-blur px-2 py-1 border border-white/10">
            {course.kind === "workshop" ? "WORKSHOP" : "COURSE"}
          </span>
        </div>
        <div className="p-6 md:p-7 flex flex-1 items-end justify-between gap-4">
          <div className="flex-1">
            <p className="font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-2">
              {course.kind === "workshop" ? "SHORT TERM" : "STORY → SCREEN"}
            </p>
            <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight text-lima-paper line-clamp-2 min-h-[2.5em]">{course.title}</h3>
            <p className="mt-2 font-mono2 text-xs tracking-[0.2em] text-[#6B73FF]">
              {(course.fee || "FEE ON REQUEST").toUpperCase()}
            </p>
          </div>
          <ArrowDownRight size={22} className="shrink-0 text-lima-blue group-hover:translate-x-1 group-hover:translate-y-1 transition-transform duration-300" />
        </div>
      </Link>
    </motion.div>
  );
}

export default function CoursesSection({ courses }) {
  return (
    <section id="programs" className="bg-lima-cream text-lima-ink px-5 md:px-10 py-14 md:py-32" data-testid="programs-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-16">
        <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
          Choose your craft.
          <br />
          <span className="font-serifit font-normal text-[#3a3a36]">Build real work.</span>
        </h2>
        <p className="max-w-xs text-base text-black/55 leading-relaxed">
          Every program is built around one idea: learn by doing, on live work, for real clients.
        </p>
      </div>
      {courses.length === 0 ? (
        <p className="font-mono2 text-xs tracking-[0.2em] text-black/40" data-testid="courses-loading">
          LOADING PROGRAMS…
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
          {courses.map((c, i) => (
            <CourseCard key={c.id} course={c} index={i} />
          ))}
        </div>
      )}
    </section>
  );
}
